/**
 * Shared delivery date rules for Bella cart drawer and pre-order PDP modal.
 *
 * Defaults (overridable via Theme settings → Pre-order):
 *   Cart Dubai — same-day until cutoff hour Asia/Dubai (default 19:00); after that, tomorrow.
 *   Cart Other Emirates — today disabled (earliest tomorrow).
 *   Pre-order — today disabled by default (earliest tomorrow) per region setting.
 */
(function () {
  const SHOP_TIMEZONE = 'Asia/Dubai';

  function readInt(value, fallback) {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  }

  function cfg() {
    const s = (window.theme && window.theme.bellaDeliveryRules) || {};
    return {
      cutoffHour: Math.min(23, Math.max(0, readInt(s.dubaiCutoffHour, 19))),
      cartOtherLeadDays: Math.min(14, Math.max(1, readInt(s.cartOtherLeadDays, 1))),
      pdpDubaiLeadDays: Math.min(14, Math.max(0, readInt(s.pdpDubaiLeadDays, 1))),
      pdpOtherLeadDays: Math.min(14, Math.max(0, readInt(s.pdpOtherLeadDays, 1))),
    };
  }

  function startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  function addDays(d, n) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
  }

  /** Current date/time in the shop timezone (UAE), not the shopper's browser clock. */
  function nowInShopTz() {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: SHOP_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).formatToParts(new Date());
      const get = (type) => parseInt(parts.find((p) => p.type === type).value, 10);
      return new Date(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
    } catch (e) {
      return new Date();
    }
  }

  function isoDate(d) {
    const x = new Date(d);
    const y = x.getFullYear();
    const m = String(x.getMonth() + 1).padStart(2, '0');
    const day = String(x.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  function parseISODate(s) {
    if (!s) return null;
    const [y, m, d] = s.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }

  function getCartMinSelectableDate(region) {
    const { cutoffHour, cartOtherLeadDays } = cfg();
    const now = nowInShopTz();
    const today = startOfDay(now);
    if (region === 'other') {
      return startOfDay(addDays(today, cartOtherLeadDays));
    }
    // Dubai only: same-day until cutoff hour (Asia/Dubai).
    if (now.getHours() >= cutoffHour) {
      return startOfDay(addDays(today, 1));
    }
    return today;
  }

  function getPdpMinSelectableDate(region) {
    const { pdpDubaiLeadDays, pdpOtherLeadDays } = cfg();
    const today = startOfDay(nowInShopTz());
    if (region === 'other') {
      return startOfDay(addDays(today, pdpOtherLeadDays));
    }
    return startOfDay(addDays(today, pdpDubaiLeadDays));
  }

  /** @param {'cart'|'pdp'} [context] */
  function getMinSelectableDate(region, context) {
    if (context === 'pdp') return getPdpMinSelectableDate(region);
    return getCartMinSelectableDate(region);
  }

  /** @param {'cart'|'pdp'} [context] */
  function isBeforeMinSelectable(date, region, context) {
    const min = getMinSelectableDate(region, context);
    return startOfDay(date).getTime() < min.getTime();
  }

  function monthLabel(d, lang) {
    return d.toLocaleDateString(lang || document.documentElement.lang || undefined, {
      month: 'long',
      year: 'numeric',
    });
  }

  window.BellaDeliveryRules = {
    get CUTOFF_HOUR() {
      return cfg().cutoffHour;
    },
    SHOP_TIMEZONE,
    get CART_OTHER_LEAD_DAYS() {
      return cfg().cartOtherLeadDays;
    },
    get PDP_DUBAI_LEAD_DAYS() {
      return cfg().pdpDubaiLeadDays;
    },
    get PDP_OTHER_LEAD_DAYS() {
      return cfg().pdpOtherLeadDays;
    },
    startOfDay,
    addDays,
    nowInShopTz,
    isoDate,
    parseISODate,
    getCartMinSelectableDate,
    getPdpMinSelectableDate,
    getMinSelectableDate,
    isBeforeMinSelectable,
    monthLabel,
  };
})();
